import type { AxiosInstance } from 'axios';

export interface OrderGroupSummary {
  id: string;
  orderNumber: string;
  status: string;
  grandTotalPaise: number;
  createdAt: string;
  customer: { id: string; name: string; email: string };
  subOrders: Array<{
    id: string;
    vendorId: string;
    status: string;
    totalPaise: number;
    /** The package's coupon share. */
    discountPaise?: number;
    isInHouse: boolean;
    vendor: { storeName: string };
    _count: { lineItems: number };
  }>;
  payments: Array<{ provider: string; method: string | null; status: string }>;
}

/** One row of the admin's return queue. */
export interface AdminReturnRow {
  id: string;
  status: string;
  reason: string;
  photoUrls: string[];
  autoApproved: boolean;
  qcDeadlineAt: string | null;
  qcNote: string | null;
  qcPhotoUrls: string[];
  createdAt: string;
  updatedAt: string;
  items: Array<{ id: string; qty: number; lineItem: { productTitle: string; qty: number } }>;
  subOrder: {
    id: string;
    vendor: { storeName: string };
    orderGroup: { id: string; orderNumber: string; customer: { name: string } | null };
  };
}

/** A return back with the seller and waiting for their inspection. */
export interface VendorQcReturn {
  id: string;
  status: 'QC_PENDING';
  reason: string;
  photoUrls: string[];
  arrivedAt: string | null;
  qcDeadlineAt: string | null;
  createdAt: string;
  subOrder: { id: string; orderGroup: { orderNumber: string } };
  items: Array<{
    id: string;
    qty: number;
    lineItem: { id: string; productTitle: string; sku: string; unitPricePaise: number };
  }>;
}

export interface OrderGroupDetail extends OrderGroupSummary {
  billingAddress: unknown;
  shippingAddress: unknown;
  itemsSubtotalPaise: number;
  taxPaise: number;
  discountPaise: number;
  subOrders: Array<{
    id: string;
    vendorId: string;
    status: string;
    /** This package's own money, so a cancelled one can be left out (orderBilling). */
    itemsSubtotalPaise: number;
    shippingPaise: number;
    taxPaise: number;
    /** Its share of the platform coupon. */
    discountPaise: number;
    totalPaise: number;
    /** Already paid from the customer's wallet. */
    walletAppliedPaise: number;
    isInHouse: boolean;
    createdAt: string;
    deliveredAt?: string | null;
    vendor: { id: string; storeName: string; slug: string };
    _count: { lineItems: number };
    lineItems: Array<{
      id: string;
      productTitle: string;
      sku: string;
      qty: number;
      unitPricePaise: number;
      lineSubtotalPaise: number;
      taxPaise: number;
      /** The product's current slug, for linking back to it. */
      productSlug: string | null;
      /** The product's current image. Null for a product with no media. */
      imageUrl: string | null;
      /** Days this line may be returned after delivery; 0 = not returnable. */
      returnWindowDays?: number;
      /** This line's share of the order coupon, tax-inclusive. */
      couponSharePaise?: number;
    }>;
    statusEvents: Array<{
      id: string;
      fromStatus: string | null;
      toStatus: string;
      note: string | null;
      createdAt: string;
    }>;
    shipment: {
      id: string;
      status: string;
      awbNumber: string | null;
      courierName: string | null;
      pickedUpAt?: string | null;
      updatedAt?: string | null;
      estimatedDeliveryAt?: string | null;
    } | null;
    returnWindowDays?: number;
    returnRequests?: Array<{
      id: string;
      status:
        | 'REQUESTED'
        | 'APPROVED'
        | 'REJECTED'
        | 'PICKED_UP'
        | 'QC_PENDING'
        | 'QC_FAILED'
        | 'RECEIVED'
        | 'REFUNDED';
      reason: string;
      resolution?: string | null;
      refundPaise?: number | null;
      /** The customer's photos of the item. */
      photoUrls?: string[];
      /** Approved by the auto-approve rules rather than an admin. */
      autoApproved?: boolean;
      /** Back with the seller at, and the end of their inspection window. */
      arrivedAt?: string | null;
      qcDeadlineAt?: string | null;
      /** The seller's inspection finding and photos. */
      qcNote?: string | null;
      qcPhotoUrls?: string[];
      qcAt?: string | null;
      refundedAt?: string | null;
      createdAt: string;
      items: Array<{ id: string; lineItemId: string; qty: number }>;
      creditNote?: { id: string; creditNoteNumber: string } | null;
      returnShipment?: {
        id: string;
        status: string;
        awbNumber: string | null;
        courierName: string | null;
        pickupScheduledAt?: string | null;
        pickedUpAt?: string | null;
        deliveredAt?: string | null;
      } | null;
    }>;
    cancellationCreditNote?: { id: string; creditNoteNumber: string } | null;
    /**
     * Why a cancelled package was cancelled, and by whom. Only on the
     * customer's own order (`getMyOrder`); null while it is not cancelled.
     */
    cancellation?: {
      cancelledBy: 'SELLER' | 'CUSTOMER' | 'SUPPORT' | 'SYSTEM';
      reason: string | null;
      cancelledAt: string | null;
    } | null;
    taxInvoice?: {
      id: string;
      invoiceNumber: string;
    } | null;
    codRemittance?: {
      id: string;
      status: string;
    } | null;
    settlement?: {
      id: string;
      status: string;
      grossPaise: number;
      commissionPaise: number;
      netPayablePaise: number;
      reserveHeldPaise: number;
    } | null;
  }>;
  payments: Array<{
    id: string;
    provider: string;
    method: string | null;
    status: string;
    amountPaise: number;
    refunds?: Array<{
      id: string;
      amountPaise: number;
      status: string;
      reason: string | null;
    }>;
  }>;
}

/** A GST tax invoice as issued: every party and line is a snapshot frozen at issue time. */
export interface TaxInvoiceDocument {
  id: string;
  subOrderId: string;
  invoiceNumber: string;
  financialYear: string;
  issuedAt: string;
  isInterState: boolean;
  sellerSnapshot: {
    legalName: string;
    /** Dispatch address (invoices issued after it was recorded). */
    address?: string | null;
    pincode?: string | null;
    gstin: string | null;
    pan: string | null;
    city: string | null;
    state: string | null;
    stateCode: string | null;
    email: string | null;
    phone: string | null;
  };
  buyerSnapshot: {
    name: string | null;
    email: string | null;
    phone: string | null;
    placeOfSupplyStateCode: string | null;
    orderNumber?: string;
    orderedAt?: string;
    shippingAddress: {
      contactName?: string;
      contactPhone?: string;
      line1?: string;
      line2?: string;
      city?: string;
      state?: string;
      pincode?: string;
      country?: string;
    } | null;
  };
  lineSnapshot: Array<{
    description: string;
    sku: string;
    hsnSac: string;
    qty: number;
    unitPricePaise: number;
    discountPaise: number;
    taxableValuePaise: number;
    gstRate: number;
    cgstPaise: number;
    sgstPaise: number;
    igstPaise: number;
    lineTotalPaise: number;
  }>;
  taxableValuePaise: number;
  cgstPaise: number;
  sgstPaise: number;
  igstPaise: number;
  totalTaxPaise: number;
  grandTotalPaise: number;
  /** Platform-funded coupon; the buyer paid `grandTotalPaise - platformDiscountPaise`. */
  platformDiscountPaise?: number;
}

export interface OrderListResponse {
  data: OrderGroupSummary[];
  page: { page: number; pageSize: number; total: number; pageCount: number };
}

/**
 * What checkout would charge for a cart delivered to an address. The server
 * prices it with the same function checkout writes the order with, so
 * `summary.grandTotalPaise` is the amount the order will carry and the gateway
 * will ask for. Re-fetch on every address or payment-method change.
 */
export interface CheckoutQuote {
  cartId: string;
  /** Prices moved since the cart was filled; the summary already uses the new ones. */
  pricesChanged: boolean;
  summary: {
    itemsSubtotalPaise: number;
    discountPaise: number;
    /** Goods GST plus GST on delivery. */
    taxPaise: number;
    shippingPaise: number;
    /** GST on delivery, at the goods' rates; already in `taxPaise`. */
    shippingTaxPaise?: number;
    grandTotalPaise: number;
  };
  shipping: {
    pricing: 'FLAT' | 'QUOTED';
    freeShippingApplied: boolean;
    /** False when delivery cannot be priced or no courier serves the address. */
    canCheckout: boolean;
    message: string | null;
    packages: Array<{
      vendorId: string;
      shippingPaise: number;
      shippingTaxPaise?: number;
      status: 'OK' | 'NOT_SERVICEABLE' | 'UNAVAILABLE' | 'NOT_CONFIGURED' | 'NO_PICKUP';
      /** COURIER: the courier's quote. ZONE_RATE: the courier could not be asked. */
      pricedBy?: 'COURIER' | 'ZONE_RATE' | 'FLAT' | null;
      estimatedDeliveryDays: number | null;
    }>;
  };
}

export interface VendorCourierCharges {
  /** Whether the marketplace policy makes the seller pay the courier. */
  sellerPaysCourier: boolean;
  /** What the customer paid for this package's delivery. */
  customerDeliveryChargePaise: number;
  charges: Array<{
    id: string;
    type: string;
    awbNumber: string | null;
    status: 'ESTIMATED' | 'RECONCILED' | 'PENDING_EVIDENCE';
    estimatedPaise: number | null;
    actualPaise: number | null;
    chargedToYouPaise: number;
    occurredAt: string;
  }>;
}

export function orderEndpoints(http: AxiosInstance) {
  return {
    // customer
    checkout: (
      body: {
        cartId: string;
        billingAddress: Record<string, string>;
        shippingAddress: Record<string, string>;
        paymentMethod?: string;
        fulfillmentType?: 'DELIVERY' | 'PICKUP';
      },
      /** Send one per tap of "Place order"; a retried request then makes one order. */
      idempotencyKey?: string,
    ) =>
      http
        .post<OrderGroupDetail>('/orders/checkout', body, {
          ...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
        })
        .then((r) => r.data),

    quoteCheckout: (body: {
      cartId: string;
      shippingAddress: Record<string, string>;
      paymentMethod?: string;
      fulfillmentType?: 'DELIVERY' | 'PICKUP';
    }) => http.post<CheckoutQuote>('/orders/checkout/quote', body).then((r) => r.data),

    /** `filter` is one of `MY_ORDER_FILTERS` (from @terra/utils): it narrows by package status. */
    myOrders: (params?: { page?: number; pageSize?: number; filter?: string }) =>
      http.get<OrderListResponse>('/orders/me', { params }).then((r) => r.data),

    getMyOrder: (orderGroupId: string) =>
      http.get<OrderGroupDetail>(`/orders/me/${orderGroupId}`).then((r) => r.data),

    cancelMyOrder: (orderGroupId: string, reason: string) =>
      http
        .post<OrderGroupDetail>(`/orders/me/${orderGroupId}/cancel`, { reason })
        .then((r) => r.data),

    /** Cancel one package of the order, while its seller has not packed it. */
    cancelMyPackage: (orderGroupId: string, subOrderId: string, reason: string) =>
      http
        .post<OrderGroupDetail>(`/orders/me/${orderGroupId}/packages/${subOrderId}/cancel`, {
          reason,
        })
        .then((r) => r.data),

    /** The package's GST invoice. The buyer, its seller and admin staff may read it. */
    getTaxInvoice: (subOrderId: string) =>
      http.get<TaxInvoiceDocument>(`/orders/sub/${subOrderId}/invoice`).then((r) => r.data),

    requestReturn: (
      subOrderId: string,
      body: {
        /** Required for reasons about the item itself (returnReasonNeedsPhoto). */
        photoUrls?: string[];
        reason: string;
        items: Array<{ lineItemId: string; qty: number }>;
        /** Cash on delivery only: refund to the wallet, or pay out to a UPI ID. */
        refundMode?: 'WALLET' | 'UPI';
        refundUpiId?: string;
      },
    ) => http.post(`/orders/sub/${subOrderId}/return`, body).then((r) => r.data),

    // vendor
    vendorOrders: (params?: {
      page?: number;
      pageSize?: number;
      /** One sub-order status, or several comma-separated. */
      status?: string;
      /** One shipment (parcel) status, or several comma-separated. */
      shipmentStatus?: string;
      q?: string;
      fromDate?: string;
      toDate?: string;
    }) => http.get('/vendor/orders', { params }).then((r) => r.data),

    getVendorSubOrder: (subOrderId: string) =>
      http.get(`/vendor/orders/${subOrderId}`).then((r) => r.data),

    transitionSubOrder: (subOrderId: string, body: { status: string; note?: string }) =>
      http.patch(`/vendor/orders/${subOrderId}/status`, body).then((r) => r.data),

    // admin
    listAll: (params?: {
      page?: number;
      pageSize?: number;
      q?: string;
      status?: string;
      fulfillmentType?: 'DELIVERY' | 'PICKUP';
      channel?: 'INHOUSE' | 'SELLER';
    }) => http.get<OrderListResponse>('/admin/orders', { params }).then((r) => r.data),

    getOrderGroup: (orderGroupId: string) =>
      http.get<OrderGroupDetail>(`/admin/orders/${orderGroupId}`).then((r) => r.data),

    adminCancelOrder: (orderGroupId: string, reason: string) =>
      http.post(`/admin/orders/${orderGroupId}/cancel`, { reason }).then((r) => r.data),

    adminDeleteOrder: (orderGroupId: string) =>
      http.delete<{ success: true }>(`/admin/orders/${orderGroupId}`).then((r) => r.data),

    bulkCancelOrders: (ids: string[], reason: string) =>
      http
        .post<{
          successful: number;
          failed: number;
          errors: string[];
        }>(`/admin/orders/bulk-cancel`, { ids, reason })
        .then((r) => r.data),

    adminTransitionSubOrder: (subOrderId: string, body: { status: string; note?: string }) =>
      http.patch(`/admin/orders/sub/${subOrderId}/status`, body).then((r) => r.data),

    /** Approves and books the reverse pickup; `pickupError` says if the booking failed. */
    approveReturn: (returnId: string, fault?: 'SELLER' | 'CUSTOMER') =>
      http
        .post<{
          id: string;
          status: string;
          pickupError: string | null;
        }>(`/admin/returns/${returnId}/approve`, fault ? { fault } : {})
        .then((r) => r.data),

    /** The goods are back: refunds the customer, restocks, reverses the settlement. */
    receiveReturn: (returnId: string) =>
      http.post(`/admin/returns/${returnId}/receive`).then((r) => r.data),

    rejectReturn: (returnId: string, resolution: string) =>
      http.post(`/admin/returns/${returnId}/reject`, { resolution }).then((r) => r.data),

    /** The admin's return queue, one state at a time (REQUESTED, QC_FAILED…). */
    adminReturns: (params?: { status?: string; page?: number; pageSize?: number }) =>
      http
        .get<{
          data: AdminReturnRow[];
          page: { page: number; pageSize: number; total: number; pageCount: number };
        }>('/admin/returns', { params })
        .then((r) => r.data),

    /** Rule on a return the seller disputed: refund the customer, or reject it. */
    resolveReturnDispute: (returnId: string, decision: 'REFUND' | 'REJECT', resolution: string) =>
      http.post(`/admin/returns/${returnId}/resolve`, { decision, resolution }).then((r) => r.data),

    // Seller inspection (QC) of returns back with them
    /** Returns waiting for this seller's inspection, soonest deadline first. */
    vendorQcReturns: () =>
      http.get<{ data: VendorQcReturn[] }>('/vendor/returns/qc').then((r) => r.data.data),
    /** The returned item is fine: refunds the customer. */
    passReturnQc: (returnId: string) =>
      http.post(`/vendor/returns/${returnId}/qc-pass`).then((r) => r.data),
    /** What came back is not what was sold: holds the refund for an admin's ruling. */
    failReturnQc: (returnId: string, body: { note: string; photoUrls: string[] }) =>
      http.post(`/vendor/returns/${returnId}/qc-fail`, body).then((r) => r.data),

    inHouseOrders: (params?: { page?: number; pageSize?: number; status?: string }) =>
      http.get('/admin/inhouse-orders', { params }).then((r) => r.data),

    // shipping integration
    /**
     * Pack and register the parcel with the courier. Pass the same
     * `idempotencyKey` when retrying an attempt the server may not have
     * answered, so a retry is recognised as the same request.
     */
    createShipment: (
      subOrderId: string,
      body: { weightGrams?: number; courierName?: string },
      opts?: { idempotencyKey?: string },
    ) =>
      http
        .post(`/vendor/orders/${subOrderId}/shipment`, body, {
          ...(opts?.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {}),
        })
        .then((r) => r.data),

    getShipment: (subOrderId: string) =>
      http.get(`/shipping/sub/${subOrderId}`).then((r) => r.data),

    /**
     * The calling vendor's own shipments. Scope comes from the session, so there
     * is deliberately no vendor id to pass — and passing one would do nothing.
     */
    vendorShipments: (params?: {
      page?: number;
      pageSize?: number;
      sortBy?: 'createdAt' | 'updatedAt' | 'status';
      sortDir?: 'asc' | 'desc';
      status?: string;
      q?: string;
      fromDate?: string;
      toDate?: string;
    }) => http.get('/vendor/shipping', { params }).then((r) => r.data),

    updateShipmentStatus: (
      shipmentId: string,
      status: string,
      body?: { description?: string; location?: string },
    ) => http.patch(`/shipping/${shipmentId}/status`, { status, ...body }).then((r) => r.data),

    listShipments: (params?: { page?: number; pageSize?: number; status?: string; q?: string }) =>
      http.get('/admin/shipping', { params }).then((r) => r.data),

    getShipmentByAwb: (awbNumber: string) =>
      http.get(`/admin/shipping/awb/${awbNumber}`).then((r) => r.data),

    updateProviderDetails: (
      shipmentId: string,
      body: {
        providerShipmentId?: string;
        providerOrderId?: string;
        awbNumber?: string;
        courierName?: string;
      },
    ) => http.patch(`/admin/shipping/${shipmentId}/provider-details`, body).then((r) => r.data),

    createTrackingEvent: (
      shipmentId: string,
      body: {
        status: string;
        description?: string;
        location?: string;
        occurredAt?: string;
      },
    ) => http.post(`/admin/shipping/${shipmentId}/tracking-event`, body).then((r) => r.data),

    getServiceableRates: (shipmentId: string) =>
      http.get<any>(`/shipping/${shipmentId}/rates`).then((r) => r.data),

    /**
     * Book a courier. Omit `courierId` to let the configured selection policy
     * choose; pass one to override it. A blocked courier is refused either way.
     */
    bookCourier: (shipmentId: string, courierId?: number) =>
      http
        .post<any>(`/shipping/${shipmentId}/book-courier`, courierId ? { courierId } : {})
        .then((r) => r.data),

    /** Label, manifest and courier invoice. Any of them may be null if the courier could not produce it. */
    downloadDocuments: (shipmentId: string) =>
      http
        .get<{
          labelUrl: string | null;
          manifestUrl: string | null;
          invoiceUrl: string | null;
        }>(`/shipping/${shipmentId}/documents`)
        .then((r) => r.data),

    /** Finish a booking left at AWB_ASSIGNED because the pickup call failed. */
    retryPickup: (shipmentId: string) =>
      http.post<any>(`/shipping/${shipmentId}/retry-pickup`).then((r) => r.data),

    /** Cancel with the courier as well as locally. Refused once the parcel has been collected. */
    cancelShipment: (shipmentId: string, reason: string) =>
      http.post<any>(`/shipping/${shipmentId}/cancel`, { reason }).then((r) => r.data),

    /** Book (or retry booking) the reverse pickup for an approved return. */
    createReturnShipment: (returnRequestId: string) =>
      http.post<any>(`/shipping/returns/${returnRequestId}/shipment`).then((r) => r.data),

    /** Courier costs the seller bears on their own package: estimate, actual, charged so far. */
    vendorCourierCharges: (subOrderId: string) =>
      http
        .get<VendorCourierCharges>(`/vendor/orders/${subOrderId}/courier-charges`)
        .then((r) => r.data),
  };
}

export type OrderEndpoints = ReturnType<typeof orderEndpoints>;
